import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  POST_CATEGORIES,
  type PostCategory,
} from '../../../shared/services/opportunities.service';

type Props = {
  active: PostCategory | null;
  onChange: (next: PostCategory | null) => void;
};

/**
 * Horizontal scrollable chip strip — one chip per fixed category, plus an
 * "All" chip at the head. The scrollbar is hidden (same CSS we use in the
 * inbox) so the row scrolls smoothly on mobile without a visible bar.
 */
export function CategoryChips({ active, onChange }: Props) {
  const { t } = useTranslation();

  return (
    <Box
      role="tablist"
      sx={{
        display: 'flex',
        gap: 1,
        py: 0.5,
        // Mobile: single line with hidden horizontal scroll (saves vertical
        // space). Desktop: wrap so every chip is visible at once — the
        // previous overflow-x-only layout truncated the last chip behind
        // the container edge because we hide the scrollbar.
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        overflowX: { xs: 'auto', sm: 'visible' },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Chip
        label={t('opportunities.categoryAll')}
        clickable
        onClick={() => onChange(null)}
        color={active === null ? 'primary' : 'default'}
        variant={active === null ? 'filled' : 'outlined'}
        sx={{ flex: '0 0 auto', fontWeight: active === null ? 700 : 500 }}
      />
      {POST_CATEGORIES.map((c) => {
        const isActive = active === c;
        return (
          <Chip
            key={c}
            label={t(`opportunities.category.${c}`)}
            clickable
            onClick={() => onChange(isActive ? null : c)}
            color={isActive ? 'primary' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            sx={{ flex: '0 0 auto', fontWeight: isActive ? 700 : 500 }}
          />
        );
      })}
    </Box>
  );
}
